import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  TextField,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import Autocomplete from "@mui/material/Autocomplete";
import SearchIcon from "@mui/icons-material/Search";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { toast } from "react-toastify";
import ApiService from "../../../services/categoryManagementApi";

const { reportsAndAnalyticsApi } = ApiService;

// --- Date To UTC helpers ---
function toUTCStart(date) {
  if (!date) return null;
  return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0));
}
function toUTCEnd(date) {
  if (!date) return null;
  return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999));
}

const DealerReport = () => {
  const [selectedDealer, setSelectedDealer] = useState(null);
  const [dealers, setDealers] = useState([]);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [dealerSearchKey, setDealerSearchKey] = useState("");

  useEffect(() => {
    fetchDealers();
    // eslint-disable-next-line
  }, [dealerSearchKey]);

  const fetchDealers = async () => {
    try {
      const response = await reportsAndAnalyticsApi.getAllDealers({
        searchItem: dealerSearchKey,
      });
      setDealers(response?.data?.data?.result || []);
    } catch (error) {
      toast.error("Failed to fetch dealers");
    }
  };

  const fetchReport = async () => {
    if (!selectedDealer) {
      toast.error("Please select a dealer");
      return;
    }
    // Date validation
    if (fromDate && toDate) {
      if (toDate < fromDate) {
        toast.error("End date cannot be before start date.");
        return;
      }
    }
    setLoading(true);
    try {
      // Always send ISO UTC, or undefined if not set
      const payload = {
        type: "dealerPerformance",
        id: selectedDealer._id,
        startDate: fromDate ? toUTCStart(new Date(fromDate)).toISOString() : undefined,
        endDate: toDate ? toUTCEnd(new Date(toDate)).toISOString() : undefined,
        search: searchText,
      };
      const response = await reportsAndAnalyticsApi.getUserReport(payload);
      setReportData(response?.data?.data?.result || {});
    } catch (error) {
      toast.error("Failed to fetch report");
    } finally {
      setLoading(false);
    }
  };

  // --- Reset Handler ---
  const handleReset = () => {
    setFromDate(null);
    setToDate(null);
    setSelectedDealer(null);
    setSearchText("");
    setDealerSearchKey("");
    setReportData(null);
  };

  const orders = reportData?.allOrders || [];

  const exportData = orders.map(order => ({
    "Order Number": order.orderNumber,
    Products: order.products.map(x => `${x.name}-${x.qty}`).join(", "),
    Qty: order.products.reduce((acc, p) => acc + p.qty, 0),
    "Order Total": order.total,
    "Payment Method": order.paymentMethod,
    "Order Status": order.orderStatus?.stage || "-",
    "Order Date": new Date(order.createdAt).toLocaleDateString(),
    "Driver Name": order.driverDetails?.name || "-",
    "Driver Phone": order.driverDetails?.phoneNumber || "-",
    "Commission Amount": order.commissionDetails?.[0]?.amount ? parseFloat(order.commissionDetails[0].amount).toFixed(2) : "0.00",
    "Commission Status": order.commissionDetails?.[0]?.status || "N/A",
    "Commission Date": order.commissionDetails?.[0]
      ? new Date(order.commissionDetails[0].createdAt).toLocaleDateString()
      : "N/A"
  }));

  const exportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Dealer Performance");
    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(blob, `DealerPerformance_${selectedDealer?._id || "report"}.xlsx`);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box p={{ xs: 2, sm: 3 }}>
        <Typography variant="h5" gutterBottom>
          Dealer Performance Report
        </Typography>

        <Grid container spacing={2} alignItems="center" mb={2}>
          <Grid item xs={12} sm={6} md={2}>
            <DatePicker
              label="From Date"
              value={fromDate}
              onChange={(newVal) => {
                setFromDate(newVal);
                if (toDate && newVal && toDate < newVal) setToDate(null);
              }}
              disableFuture
              maxDate={toDate || undefined}
              renderInput={(params) => <TextField fullWidth {...params} />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <DatePicker
              label="To Date"
              value={toDate}
              onChange={(newVal) => setToDate(newVal)}
              disableFuture
              minDate={fromDate || undefined}
              maxDate={new Date()}
              renderInput={(params) => <TextField fullWidth {...params} />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Autocomplete
              options={dealers}
              value={selectedDealer}
              getOptionLabel={(option) => option.businessName || ""}
              isOptionEqualToValue={(option, value) => option._id === value._id}
              onInputChange={(_, value) => setDealerSearchKey(value)}
              onChange={(_, value) => setSelectedDealer(value)}
              renderInput={(params) => <TextField fullWidth {...params} label="Select Dealer" />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={1.5}>
            <Button
              fullWidth
              variant="contained"
              onClick={fetchReport}
              disabled={loading}
              sx={{ height: 52 }}
            >
              {loading ? "Loading..." : "Generate Report"}
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={1.5}>
            <Button
              fullWidth
              variant="outlined"
              color="secondary"
              onClick={handleReset}
              sx={{ height: 52 }}
            >
              Reset
            </Button>
          </Grid>
        </Grid>

        {reportData ? (
          <>
            {/* --- YOUR SUMMARY CARD + TABLE (as in your provided code) --- */}
            <Box mb={2}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={12}>
                  <Paper
                    elevation={2}
                    sx={{
                      borderRadius: 2,
                      bgcolor: "#fafbfc",
                      width: "100%",
                      boxSizing: "border-box",
                      p: 2,
                      mb: { xs: 2, md: 0 },
                    }}
                  >
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} sm={6} md={4}>
                        <Typography variant="subtitle1" mb={1} color="primary">
                          <strong>{reportData.businessName}</strong>
                        </Typography>
                        <Typography variant="body2">
                          <strong>Phone:</strong> +{reportData.countryCode}{reportData.phoneNumber || "-"}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Address:</strong>{" "}
                          {[
                            reportData.houseNumber,
                            reportData.streetNumber,
                            reportData.village,
                            reportData.commune,
                            reportData.district,
                            reportData.city,
                            reportData.postalCode
                          ]
                            .filter((part) => part && part.trim() !== "")
                            .join(", ")}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={8}>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                              Orders
                            </Typography>
                            <Typography variant="body2">
                              <strong>Total:</strong> {reportData.totalOrders ?? "-"}
                              <br />
                              <span style={{ color: "#1976d2" }}>
                                <strong>Pending:</strong> {reportData.pendingTotalOrders ?? "-"}
                              </span>
                              <br />
                              <span style={{ color: "#f57c00" }}>
                                <strong>Rejected:</strong> {reportData.rejectedTotalOrders ?? "-"}
                              </span>
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                              Sales ($)
                            </Typography>
                            <Typography variant="body2">
                              <strong>Total:</strong> {reportData.totalSales ? Number(reportData.totalSales).toFixed(2) : "0.00"}
                              <br />
                              <span style={{ color: "#1976d2" }}>
                                <strong>Pending:</strong> {reportData.pendingTotalSales ? Number(reportData.pendingTotalSales).toFixed(2) : "0.00"}
                              </span>
                            </Typography>

                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                              Commission ($)
                            </Typography>
                            <Typography variant="body2">
                              <strong>Pending Amount:</strong> {reportData.pendingCommissionAmount ? parseFloat(reportData.pendingCommissionAmount).toFixed(2) : "0.00"}
                              <br />
                              <strong>Pending Count:</strong> {reportData.pendingCommissionCount ? parseFloat(reportData.pendingCommissionCount).toFixed(2) : "0.00"}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                              Order Value
                            </Typography>
                            <Typography variant="body2">
                              <strong>Avg Order Value:</strong> {reportData.avgOrderValue ? parseFloat(reportData.avgOrderValue).toFixed(2) : "-"}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={12}>
                  <Box display="flex" justifyContent={{ xs: "flex-start", md: "flex-end" }} width="100%" sx={{ mb: 1 }}>
                    <Button variant="outlined" onClick={exportToExcel}>
                      Export Excel
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>
            {/* TABLE (as before) */}
            <TableContainer component={Paper} sx={{ overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Order #</TableCell>
                    <TableCell>Products</TableCell>
                    <TableCell>Qty</TableCell>
                    <TableCell>Total</TableCell>
                    <TableCell>Payment</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Driver</TableCell>
                    <TableCell>Phone</TableCell>
                    <TableCell>Commission</TableCell>
                    <TableCell>Comm. Status</TableCell>
                    <TableCell>Comm. Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orders.length > 0 ? (
                    orders.map((order, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{order.orderNumber}</TableCell>
                        <TableCell>{order.products.map(x => `${x.name}-${x.qty}`).join(", ")}</TableCell>
                        <TableCell>{order.products.reduce((acc, p) => acc + p.qty, 0)}</TableCell>
                        <TableCell>{order.total ? parseFloat(order.total).toFixed(2) : "0.00"}</TableCell>
                        <TableCell>{order.paymentMethod}</TableCell>
                        <TableCell>{order.orderStatus?.stage || "-"}</TableCell>
                        <TableCell>{new Date(order.createdAt).toLocaleDateString()}</TableCell>
                        <TableCell>{order.driverDetails?.name || "-"}</TableCell>
                        <TableCell>{order.driverDetails?.phoneNumber || "-"}</TableCell>
                        <TableCell>{order.commissionDetails?.[0]?.amount || 0}</TableCell>
                        <TableCell>{order.commissionDetails?.[0]?.status || "N/A"}</TableCell>
                        <TableCell>
                          {order.commissionDetails?.[0]
                            ? new Date(order.commissionDetails[0].createdAt).toLocaleDateString()
                            : "N/A"}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={12} align="center">
                        No Data Found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        ) : (
          // --- Initial Card, shown ONLY before fetching any report ---
          <Paper
            elevation={2}
            sx={{
              mt: 4,
              width: "100%",
              minHeight: "35vh",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "#fafbfc",
              boxSizing: "border-box",
              p: { xs: 2, sm: 4 },
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              Select a date range and dealer above, then click <strong>Generate Report</strong> to view details.
            </Typography>

          </Paper>
        )}

      </Box>
    </LocalizationProvider>
  );
};

export default DealerReport;
